import { useNavigate } from "react-router-dom";
import { Lock, Sparkles, Crown, CheckCircle2 } from "lucide-react";
import Layout from "@/components/Layout";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";

const COPY = {
  premium: {
    icon: Sparkles,
    title: "AI Chat & Planner are locked",
    body: "Your free trial has ended. Upgrade to unlock unlimited AI chat with KAAYA, smart day planning and personalised recipes.",
    perks: ["Unlimited AI conversations with KAAYA", "Smart day & meal planning", "Personalised recipe cards"],
  },
  tracker: {
    icon: Crown,
    title: "Your Calorie Tracker access has ended",
    body: "Your 30-day free Tracker access is over. Upgrade to keep logging meals, workouts and water — and hold on to your streak.",
    perks: ["Daily calorie & macro logging", "Workout & water tracking", "Trends, history & insights"],
  },
};

export default function UpgradeLock({ kind = "premium" }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const c = COPY[kind] || COPY.premium;
  const Icon = c.icon;
  const trackerStillOn = kind === "premium" && user?.tracker_active === true;

  return (
    <Layout>
      <div className="flex-1 flex items-center justify-center px-5 py-10 min-h-[60vh]" data-testid={`upgrade-lock-${kind}`}>
        <div className="w-full max-w-md text-center">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-secondary/15 flex items-center justify-center mb-5 relative">
            <Icon size={28} className="text-secondary" strokeWidth={1.75} />
            <span className="absolute -bottom-1.5 -right-1.5 w-7 h-7 rounded-full bg-primary flex items-center justify-center border-2 border-background">
              <Lock size={13} className="text-primary-foreground" strokeWidth={2.5} />
            </span>
          </div>

          <h1 className="font-serif-display text-2xl sm:text-3xl text-primary tracking-tight" data-testid="upgrade-lock-title">
            {c.title}
          </h1>
          <p className="text-foreground/70 text-sm mt-3 leading-relaxed">{c.body}</p>

          <div className="mt-6 rounded-2xl border border-border bg-surface-elevated p-4 text-left space-y-2.5">
            {c.perks.map((p, i) => (
              <div key={i} className="flex items-center gap-2.5 text-sm text-foreground/85">
                <CheckCircle2 size={17} className="text-secondary shrink-0" />
                {p}
              </div>
            ))}
          </div>

          {trackerStillOn && (
            <div className="mt-4 flex items-center justify-center gap-2 text-xs text-secondary font-medium" data-testid="tracker-still-active-note">
              <CheckCircle2 size={14} /> Good news — your Calorie Tracker is still active.
            </div>
          )}

          <Button
            onClick={() => navigate("/billing")}
            data-testid="upgrade-lock-cta"
            className="mt-7 w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground text-base font-medium"
          >
            <Crown size={17} className="mr-2" /> Upgrade to unlock
          </Button>
          {trackerStillOn && (
            <button
              onClick={() => navigate("/tracker")}
              data-testid="goto-tracker-btn"
              className="mt-3 text-sm text-foreground/70 hover:text-foreground underline underline-offset-4"
            >
              Continue to my Tracker
            </button>
          )}
        </div>
      </div>
    </Layout>
  );
}
